import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'

import { endpoints } from '@/api'
import { keys } from '@/api/keys'
import { useCan, useCapabilities } from '@/auth'
import { Alert } from '@/design'
import { formatInZone } from '@/lib/timezone'
import { smsNotice, standingNotice } from '@/features/billing/billing-copy'

/**
 * The banners every staff page carries (launch plan §5): DEMO / TEST DATA for a demo tenant, the
 * 80% and 100% SMS allowance notices, and the D8 billing standing. Each one is driven by a live
 * read the caller is allowed to make — a front-desk user without `billing:read` simply sees none
 * of the billing banners, and the demo banner needs `organizations:read`.
 */
export function WorkspaceBanners() {
  const capabilities = useCapabilities()
  const orgId = capabilities?.organizationId ?? null
  const canOrg = useCan('organizations:read')
  const canBilling = useCan('billing:read')

  const organization = useQuery({
    queryKey: keys.organization(orgId ?? 'none'),
    queryFn: () => endpoints.organizations.get(orgId ?? ''),
    enabled: canOrg && orgId !== null,
    staleTime: 5 * 60_000,
  })
  const usage = useQuery({
    queryKey: keys.billing.usage,
    queryFn: () => endpoints.billing.usage(),
    enabled: canBilling && orgId !== null,
    staleTime: 60_000,
  })

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const fmt = (instant: string): string => formatInZone(instant, tz)
  const isDemo = organization.data?.is_demo === true
  const standing = usage.data === undefined ? null : standingNotice(usage.data.billing, fmt)
  const sms =
    usage.data === undefined
      ? null
      : smsNotice(usage.data.usage.sms_segments, fmt(usage.data.period.end))
  const billingLink = (
    <Link to="/settings/billing" className="font-semibold underline-offset-4 hover:underline">
      Open Billing
    </Link>
  )

  if (!isDemo && standing === null && sms === null) return null
  return (
    <div className="mb-4 space-y-2">
      {isDemo && (
        <Alert tone="warning" title="DEMO / TEST DATA" live={false}>
          This is a demo account. Its records are fictional, it is left out of revenue reporting,
          and it is never billed.
        </Alert>
      )}
      {sms !== null && (
        <Alert tone={sms.tone} title={sms.title} live={false} actions={billingLink}>
          {sms.body}
        </Alert>
      )}
      {standing !== null && standing.tone !== 'info' && (
        <Alert tone={standing.tone} title={standing.title} live={false} actions={billingLink}>
          {standing.body}
        </Alert>
      )}
    </div>
  )
}
